/**
 * Boot diagnostics -> Firebase Analytics (native SDK, reliable inside the WKWebView).
 *
 * Purpose: after the "freezes on Home after an automatic App Store update" reports,
 * we need to know, on a real affected device, HOW FAR boot got and whether the app
 * is alive. This is observe-only: it never changes behaviour, never reloads.
 *
 * How it survives a freeze: each milestone is written synchronously to localStorage
 * (`diag_last_step`) AND mirrored to a Firebase event. If a session freezes before
 * its events upload, the milestone is still in localStorage; on the NEXT successful
 * session `reportPreviousBoot()` reads it and logs `diag_prev_boot` — which then
 * uploads. (So: after a freeze, the client must force-quit + reopen the app BEFORE
 * deleting/reinstalling, or the buffered events are lost.)
 *
 * Reserved Firebase names/prefixes are avoided; we use a few event names with params.
 */
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'

const LAST_KEY = 'diag_last_step'
const PREV_KEY = 'diag_prev_last_step'

/** Fire-and-forget Firebase event. Never throws, never blocks. */
export function diagEvent(name, params) {
  // Mirror to the on-device console so diag events are visible LIVE in eruda
  // during a freeze — not only in Firebase. Overlay hits (diag_overlay with
  // blocked:1) go through console.warn so they surface in eruda's Warning tab,
  // away from the Capgo error noise. Prefixed [DIAG] so it's easy to filter.
  try {
    const blocked = params && params.blocked === 1
    const line = ["[DIAG]", name, params || {}]
    if (blocked && typeof console.warn === "function") console.warn(...line)
    else console.log(...line)
  } catch (_) {}
  try {
    FirebaseAnalytics.logEvent({ name, params: params || {} }).catch(() => {})
  } catch (_) {
    /* plugin not ready / web fallback — ignore */
  }
}

/** Record a boot milestone: persisted (survives a freeze) + mirrored to Firebase. */
export function bootStep(step) {
  try {
    localStorage.setItem(LAST_KEY, step + '|' + Date.now())
  } catch (_) {}
  diagEvent('diag_boot', { step })
}

/**
 * Report where the PREVIOUS session stopped. Call once, early, on a healthy boot.
 * If last session froze mid-boot, this is the event that tells us the exact step.
 */
export function reportPreviousBoot(extra) {
  let prev = null
  try {
    prev = localStorage.getItem(PREV_KEY)
  } catch (_) {}
  if (prev) {
    const parts = String(prev).split('|')
    diagEvent('diag_prev_boot', {
      last_step: parts[0] || 'unknown',
      last_at: parts[1] || '',
      ...(extra || {}),
    })
  }

  // Report (and clear) the marker set by public/error.html when a WebView
  // navigation failed — tells us the freeze was a failed load, not a JS hang.
  try {
    const errAt = localStorage.getItem('diag_error_page')
    if (errAt) {
      diagEvent('diag_error_page', { at: errAt })
      localStorage.removeItem('diag_error_page')
    }
  } catch (_) {}
}

// Expose a tiny global so dependency-free modules (e.g. safeStorage) can report
// without importing this file.
try {
  window.__diag = { event: diagEvent, bootStep }
} catch (_) {}
